import {
  allGuestNames,
  bookingArrival,
  dateKeyOf,
  fmtDate,
  fmtTime,
  latestTransfer,
  stayNights,
  STATUSES_WITH_ASSIGNED_ROOM,
} from "../adminHelpers";
import { plannedDeparture, roomsText } from "../copyDetails";

/* Which paper form a guest house uses, by its short code. SSB and the Faculty
   Guest House each have their own; the Lakshman Das houses and the University
   Guest House share one, with a box per house. */
export const FORM_KINDS = {
  ssb: "Shanti Swaroop Bhatnagar Guest House",
  faculty: "Faculty Guest House",
  ld_ugh: "Lakshman Das / University Guest House",
};

const LD_UGH_TICK = { LD: "ld", LDE: "ldExt", UGH: "ugh" };

export function formKindFor(shortName) {
  const code = String(shortName || "").toUpperCase();
  if (code === "SSB") return "ssb";
  if (code === "FGH") return "faculty";
  return "ld_ugh";
}

/* The house the form is for: where the guest actually has a room once one is
   allotted; for a transferred indent, the house it was sent to; otherwise the
   house asked for first, otherwise the indent's own. */
function formGuestHouse(booking, guestHouses) {
  const byId = new Map((guestHouses || []).map((g) => [Number(g.id), g]));
  if (STATUSES_WITH_ASSIGNED_ROOM.includes(booking.status)) {
    const a = (booking.roomAssignments || []).find((x) => x.roomNumber && !x.isNoShow);
    if (a) {
      const gh = byId.get(Number(a.guestHouseId));
      return { id: a.guestHouseId, shortName: a.guestHouseShortName || gh?.shortName || "", name: a.guestHouseName || gh?.name || "" };
    }
  }
  if (latestTransfer(booking) && booking.guestHouseId) {
    const gh = byId.get(Number(booking.guestHouseId));
    return {
      id: booking.guestHouseId,
      shortName: booking.guestHouseShortName || gh?.shortName || "",
      name: booking.guestHouseName || gh?.name || "",
    };
  }
  const pref = booking.guestHousePreferenceOptions?.[0];
  const id = pref?.id ?? booking.guestHouseId;
  const gh = byId.get(Number(id));
  return { id, shortName: gh?.shortName || "", name: gh?.name || pref?.name || booking.guestHouseName || "" };
}

/* The rooms allotted, by number, for the top of the form; blank until rooms
   are assigned so the line is left for a hand. A room held twice prints once. */
function allottedRoomNumbers(booking) {
  if (!STATUSES_WITH_ASSIGNED_ROOM.includes(booking.status)) return "";
  const numbers = (booking.roomAssignments || [])
    .filter((a) => a.roomNumber && !a.isNoShow)
    .map((a) => String(a.roomNumber).trim());
  return [...new Set(numbers)].join(", ");
}

/* One room's guest field often names several people — "Dr. A + Dr. B",
   "Mr. C & Mr. D", "E, F" — and each is a person of their own on paper. */
export function splitGuestNames(text) {
  return String(text ?? "")
    .split(/[,+&]/)
    .map((s) => s.trim())
    .filter(Boolean);
}

/* Every person accompanying the main guest, one entry each: the rooms' guest
   names (the main guest is item 1, not one of these), split and
   de-duplicated. */
function accompanyingNamesOf(booking) {
  const seen = new Set();
  const names = [];
  for (const entry of allGuestNames(booking).slice(1)) {
    for (const name of splitGuestNames(entry)) {
      const key = name.toLowerCase();
      if (seen.has(key)) continue;
      seen.add(key);
      names.push(name);
    }
  }
  return names;
}

/* The paper forms have room for this many accompanying persons; past it the
   full list goes on a page of its own. */
export const FORM_ACCOMPANYING_LINES = 3;

const PAYMENT_TICK = { BY_GUEST: "byGuest", BY_PERSON: "byApplicant", UNIVERSITY: "university" };

/* Everything a printed application form shows, formatted and ready to drop
   onto the form's lines. Blank string means "leave the line empty" — the
   system doesn't record it (accompanying persons' names, the applicant's
   relation to the guest) or it is for a hand to fill in. */
export function applicationFormData(booking, { guestHouses = [], paymentOptions = [] } = {}) {
  const b = booking || {};
  const gh = formGuestHouse(b, guestHouses);
  const kind = formKindFor(gh.shortName);
  const arr = bookingArrival(b, { planned: true });
  const dep = plannedDeparture(b);
  const nights = arr.date && dep.date ? stayNights(arr.date, dep.date) : "";
  const paymentCode = b.paymentResponsibility || "";
  const paymentLabel = paymentOptions.find((o) => o.code === paymentCode)?.label || b.payment || "";
  const accompanyingNames = accompanyingNamesOf(b);
  const accompanyingTotal = Math.max(Number(b.accompanyingPersonsCount) || 0, accompanyingNames.length);
  const needsGuestList = accompanyingTotal > FORM_ACCOMPANYING_LINES;
  const createdKey = dateKeyOf(b.createdAt);
  const clean = (v) => (v === "—" || v == null ? "" : String(v).trim());

  return {
    kind,
    guestHouseName: gh.name,
    ldUghTick: kind === "ld_ugh" ? LD_UGH_TICK[String(gh.shortName).toUpperCase()] || "" : "",
    bookingCode: clean(b.bookingCode),
    formDate: createdKey ? fmtDate(createdKey) : "",
    isOfficial: b.bookingTypeCode === "OFFICIAL",
    isNonOfficial: b.bookingTypeCode === "NON_OFFICIAL" || b.bookingTypeCode === "PERSONAL",

    guestName: clean(b.guestName).toUpperCase(),
    mainGuestName: clean(b.guestName),
    /* Always exactly the form's three lines, filled as far as names go. */
    formAccompanying: Array.from({ length: FORM_ACCOMPANYING_LINES }, (_, i) => accompanyingNames[i] || ""),
    accompanyingTotal,
    needsGuestList,
    /* Page 2: every named person, then blank rows up to the stated count so
       each one still has a line to be written in. */
    guestListRows: needsGuestList
      ? Array.from({ length: accompanyingTotal }, (_, i) => ({ n: i + 1, name: accompanyingNames[i] || "" }))
      : [],
    guestMobile: clean(b.guestMobileNumber),
    guestDesignation: clean(b.guestDesignation),
    guestAddress: clean(b.guestAddress),
    accompanyingCount: accompanyingTotal,
    purpose: clean(b.visitPurpose || b.purpose),
    rooms: clean(roomsText(b)),
    roomNumbers: allottedRoomNumbers(b),
    /* The raw request, for forms that tabulate room types (Faculty's item 8). */
    roomRequests: (b.guestHousePreferenceOptions?.[0]?.roomRequests || [])
      .filter((r) => r.count > 0)
      .map((r) => ({ name: r.roomTypeName || "", shortName: r.roomTypeShortName || "", capacity: Number(r.capacity) || 0, count: r.count })),
    duration: clean(nights),
    /* From the window actually printed above, so a moved or extended stay
       counts its own nights; the booking's own figure only as a fallback. */
    days: clean((typeof nights === "string" ? nights.replace(/\D+/g, "") : "") || b.numberOfDays || ""),
    arrivalDate: arr.date ? fmtDate(arr.date) : "",
    arrivalTime: clean(fmtTime(arr.time)),
    departureDate: dep.date ? fmtDate(dep.date) : "",
    departureTime: clean(fmtTime(dep.time)),

    applicantName: clean(b.applicantName || b.userName),
    employeeId: clean(b.employeeId),
    applicantDesignation: clean(b.applicantDesignation || b.designation),
    applicantDepartment: clean(b.applicantDepartment || b.department),
    applicantMobile: clean(b.applicantMobileNumber || b.phone),
    applicantEmail: clean(b.emailId || b.email),

    paymentTick: PAYMENT_TICK[paymentCode] || "",
    paymentLabel: clean(paymentLabel),
    paymentOther: paymentCode === "OTHER" ? clean(b.paymentResponsibilityOther) : "",
  };
}
